/**
 * BotSpot Web Dashboard - Hatchery Hook
 *
 * React hook for fetching gallery data and hatching new agents with real-time updates.
 */

import { useState, useEffect, useCallback } from "react";
import { getGallery, hatchAgent as hatchAgentApi } from "../api/hatchery";
import { getErrorMessage } from "../api/client";
import type { AgentIdentityPublic, HatchResponse } from "../api/types";

/**
 * Hook state interface
 */
interface UseHatcheryState {
  agents: AgentIdentityPublic[];
  total: number;
  limit: number;
  loading: boolean;
  error: string | null;
  hatching: boolean;
  hatchError: string | null;
}

/**
 * Hook return type
 */
interface UseHatcheryReturn extends UseHatcheryState {
  refetch: () => Promise<void>;
  hatchAgent: (creatorName: string) => Promise<HatchResponse | null>;
}

/**
 * Custom hook for fetching gallery data and hatching agents
 *
 * Features:
 * - Automatic fetching on mount
 * - Periodic polling for real-time updates
 * - Manual refetch capability
 * - Hatch new agents
 * - Error handling
 *
 * @param limit - Maximum number of agents to fetch (1-100, default 20)
 * @param pollInterval - Polling interval in milliseconds (default: 5000, 0 to disable)
 * @returns Gallery data, hatch function, and refetch function
 */
export function useHatchery(
  limit: number = 20,
  pollInterval: number = 5000
): UseHatcheryReturn {
  const [state, setState] = useState<UseHatcheryState>({
    agents: [],
    total: 0,
    limit: Math.max(1, Math.min(100, limit)),
    loading: true,
    error: null,
    hatching: false,
    hatchError: null,
  });

  /**
   * Fetch gallery from API
   */
  const fetchGallery = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const data = await getGallery(state.limit);

      setState((prev) => ({
        ...prev,
        agents: data.agents,
        total: data.total,
        loading: false,
        error: null,
      }));
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [state.limit]);

  /**
   * Hatch a new agent
   */
  const hatchAgent = useCallback(
    async (creatorName: string): Promise<HatchResponse | null> => {
      try {
        setState((prev) => ({ ...prev, hatching: true, hatchError: null }));

        const response = await hatchAgentApi(creatorName);

        setState((prev) => ({
          ...prev,
          hatching: false,
          hatchError: null,
        }));

        // Refetch gallery to include the new agent
        await fetchGallery();

        return response;
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setState((prev) => ({
          ...prev,
          hatching: false,
          hatchError: errorMessage,
        }));
        return null;
      }
    },
    [fetchGallery]
  );

  /**
   * Initial fetch and polling setup
   */
  useEffect(() => {
    fetchGallery();

    // Set up polling if interval is specified
    if (pollInterval > 0) {
      const intervalId = setInterval(() => {
        fetchGallery();
      }, pollInterval);

      return () => clearInterval(intervalId);
    }
  }, [fetchGallery, pollInterval]);

  return {
    ...state,
    refetch: fetchGallery,
    hatchAgent,
  };
}
